import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { TermsAudience, TermsDocumentResponse } from '../types';

interface DocumentFormData {
  title: string;
  titleAr: string;
  body: string;
  bodyAr: string;
  sortOrder: string;
  requiresReacceptance: boolean;
}

const emptyForm: DocumentFormData = {
  title: '',
  titleAr: '',
  body: '',
  bodyAr: '',
  sortOrder: '',
  requiresReacceptance: false,
};

const AUDIENCE_LABELS: Record<TermsAudience, string> = {
  PARTNER: 'Partner Portal',
  CUSTOMER: 'Mobile Customer App',
};

function normalizeAudience(value: string | null): TermsAudience {
  return value?.toUpperCase() === 'CUSTOMER' ? 'CUSTOMER' : 'PARTNER';
}

export default function TermsDocumentsPage() {
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const audience: TermsAudience = normalizeAudience(searchParams.get('audience'));

  const [documents, setDocuments] = useState<TermsDocumentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [form, setForm] = useState<DocumentFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  // #430 — preview the authored HTML the way the end user will see it, before submitting.
  const [showPreview, setShowPreview] = useState(false);

  const loadDocuments = useCallback(async () => {
    if (!auth.token) return;
    setError(null);
    setLoading(true);
    try {
      const docs = await api.getTermsDocuments(auth.token, audience);
      setDocuments(docs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [auth.token, audience]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const setAudience = (next: TermsAudience) => {
    if (next === audience) return;
    setShowForm(false);
    setEditingDocId(null);
    setForm(emptyForm);
    setShowPreview(false);
    const params = new URLSearchParams(searchParams);
    params.set('audience', next.toLowerCase());
    setSearchParams(params, { replace: false });
  };

  const sortedDocuments = [...documents].sort((a, b) => a.sortOrder - b.sortOrder);

  const handleAddNew = () => {
    setEditingDocId(null);
    setForm({
      ...emptyForm,
      sortOrder: String(documents.length + 1),
    });
    setShowForm(true);
  };

  const handleEdit = (doc: TermsDocumentResponse) => {
    setEditingDocId(doc.id);
    setForm({
      title: doc.title,
      titleAr: doc.titleAr || '',
      body: doc.body,
      bodyAr: doc.bodyAr || '',
      sortOrder: String(doc.sortOrder),
      requiresReacceptance: false,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!auth.token) return;
    setSaving(true);
    setError(null);

    try {
      if (editingDocId) {
        await api.updateTermsDocument(editingDocId, {
          title: form.title,
          titleAr: form.titleAr || undefined,
          body: form.body,
          bodyAr: form.bodyAr || undefined,
          sortOrder: form.sortOrder ? parseInt(form.sortOrder, 10) : undefined,
          requiresReacceptance: form.requiresReacceptance,
        }, auth.token);
      } else {
        await api.createTermsDocument({
          title: form.title,
          titleAr: form.titleAr || undefined,
          body: form.body,
          bodyAr: form.bodyAr || undefined,
          sortOrder: form.sortOrder ? parseInt(form.sortOrder, 10) : undefined,
          audience,
        }, auth.token);
      }

      setShowForm(false);
      setEditingDocId(null);
      setForm(emptyForm);
      setShowPreview(false);
      await loadDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (doc: TermsDocumentResponse) => {
    if (!auth.token) return;
    setError(null);
    try {
      await api.updateTermsDocumentStatus(doc.id, !doc.isActive, auth.token);
      await loadDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0 || !auth.token) return;
    const newDocs = [...sortedDocuments];
    [newDocs[index - 1], newDocs[index]] = [newDocs[index], newDocs[index - 1]];
    const documentIds = newDocs.map(d => d.id);
    try {
      await api.reorderTermsDocuments(documentIds, auth.token);
      await loadDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reorder');
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === sortedDocuments.length - 1 || !auth.token) return;
    const newDocs = [...sortedDocuments];
    [newDocs[index], newDocs[index + 1]] = [newDocs[index + 1], newDocs[index]];
    const documentIds = newDocs.map(d => d.id);
    try {
      await api.reorderTermsDocuments(documentIds, auth.token);
      await loadDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reorder');
    }
  };

  return (
    <div>
      <div className="admin-card-header" style={{ marginBottom: '1.5rem' }}>
        <h1 className="admin-card-title">Terms Documents Management</h1>
      </div>

      <div className="admin-tabs" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid var(--admin-border)' }}>
        {(['PARTNER', 'CUSTOMER'] as const).map(a => {
          const isActive = audience === a;
          return (
            <button
              key={a}
              data-testid={`audience-tab-${a.toLowerCase()}`}
              onClick={() => setAudience(a)}
              style={{
                padding: '0.625rem 1rem',
                background: 'transparent',
                border: 'none',
                borderBottom: isActive ? '2px solid var(--admin-primary, #2196f3)' : '2px solid transparent',
                color: isActive ? 'var(--admin-primary, #2196f3)' : 'var(--admin-text-muted)',
                fontWeight: isActive ? 600 : 500,
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              {AUDIENCE_LABELS[a]}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="admin-info-banner" style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="admin-loading">
          <div className="admin-loading-spinner"></div>
          <p>Loading terms documents...</p>
        </div>
      ) : (
        <div className="admin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--admin-text)', margin: 0 }}>
              {AUDIENCE_LABELS[audience]} Documents
            </h3>
            <button
              className="admin-btn admin-btn-primary admin-btn-sm"
              onClick={handleAddNew}
            >
              + Add New Document
            </button>
          </div>

          {/* Add/Edit Form */}
          {showForm && (
            <div style={{
              background: '#f8fafc',
              border: '1px solid var(--admin-border)',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem',
            }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                {editingDocId
                  ? `Edit ${AUDIENCE_LABELS[audience]} Document`
                  : `Add New ${AUDIENCE_LABELS[audience]} Document`}
              </h4>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="admin-form-group">
                  <label className="admin-form-label">Title (English) *</label>
                  <input
                    className="admin-form-input"
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g., Terms of Service"
                  />
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Title (Arabic)</label>
                  <input
                    className="admin-form-input"
                    type="text"
                    value={form.titleAr}
                    onChange={(e) => setForm({ ...form, titleAr: e.target.value })}
                    dir="rtl"
                  />
                </div>

                <div className="admin-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="admin-form-label">Body (English) *</label>
                  <textarea
                    className="admin-form-input"
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    placeholder="HTML content for the terms document..."
                    rows={6}
                    style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: '0.8rem' }}
                  />
                </div>

                <div className="admin-form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="admin-form-label">Body (Arabic)</label>
                  <textarea
                    className="admin-form-input"
                    value={form.bodyAr}
                    onChange={(e) => setForm({ ...form, bodyAr: e.target.value })}
                    rows={6}
                    dir="rtl"
                    style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: '0.8rem' }}
                  />
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">Sort Order</label>
                  <input
                    className="admin-form-input"
                    type="number"
                    min="0"
                    value={form.sortOrder}
                    onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                    placeholder="0"
                  />
                </div>

                {editingDocId && audience === 'CUSTOMER' && (
                  <div className="admin-form-group" style={{ alignSelf: 'end' }}>
                    <label
                      className="admin-form-label"
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        checked={form.requiresReacceptance}
                        onChange={(e) => setForm({ ...form, requiresReacceptance: e.target.checked })}
                        data-testid="material-change-checkbox"
                      />
                      <span>
                        Mark as material change
                        <span style={{ fontWeight: 400, fontSize: '0.75rem', color: 'var(--admin-text-muted)', display: 'block' }}>
                          Customers will be required to re-accept on next app launch
                        </span>
                      </span>
                    </label>
                  </div>
                )}
              </div>

              {showPreview && (
                <div
                  className="admin-card"
                  style={{ marginTop: '0.75rem', background: 'var(--admin-bg, #fff)' }}
                  data-testid="terms-preview"
                >
                  <div className="admin-form-label" style={{ marginBottom: '0.5rem' }}>
                    Preview — how the document will look to the user
                  </div>
                  {form.title && <h3 style={{ marginTop: 0 }}>{form.title}</h3>}
                  <div
                    className="terms-preview-body"
                    style={{ fontSize: '0.9rem', lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: form.body }}
                  />
                  {form.bodyAr && (
                    <>
                      <hr style={{ margin: '1rem 0', borderColor: 'var(--admin-border, #e5e7eb)' }} />
                      {form.titleAr && <h3 dir="rtl">{form.titleAr}</h3>}
                      <div
                        className="terms-preview-body"
                        dir="rtl"
                        style={{ fontSize: '0.9rem', lineHeight: 1.6 }}
                        dangerouslySetInnerHTML={{ __html: form.bodyAr }}
                      />
                    </>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                <button
                  className="admin-btn admin-btn-primary admin-btn-sm"
                  disabled={!form.title.trim() || !form.body.trim() || saving}
                  onClick={handleSave}
                >
                  {saving ? 'Saving...' : editingDocId ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-outline admin-btn-sm"
                  disabled={!form.body.trim()}
                  onClick={() => setShowPreview((v) => !v)}
                >
                  {showPreview ? 'Hide preview' : 'Preview'}
                </button>
                <button
                  className="admin-btn admin-btn-outline admin-btn-sm"
                  onClick={() => { setShowForm(false); setEditingDocId(null); setForm(emptyForm); setShowPreview(false); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Documents Table */}
          {sortedDocuments.length === 0 ? (
            <div className="admin-empty-state">
              <p>
                {audience === 'CUSTOMER'
                  ? 'No customer documents yet. Add Terms of Service and Privacy Policy to publish them to the mobile app.'
                  : 'No partner documents yet. Click "+ Add New Document" to create one.'}
              </p>
            </div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>#</th>
                    <th>Title</th>
                    <th>Title (AR)</th>
                    <th style={{ width: '90px' }}>Version</th>
                    <th>Sort</th>
                    <th>Status</th>
                    <th style={{ width: '220px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDocuments.map((doc, index) => (
                    <tr key={doc.id}>
                      <td style={{ color: 'var(--admin-text-muted)', fontSize: '0.8rem' }}>{index + 1}</td>
                      <td style={{ fontWeight: 500 }}>{doc.title}</td>
                      <td dir="rtl">{doc.titleAr || '—'}</td>
                      <td>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          v{doc.currentVersion}
                        </span>
                        {doc.requiresReacceptance && (
                          <span
                            title="Current version requires re-acceptance"
                            style={{ marginLeft: 6, fontSize: '0.7rem', color: '#d97706' }}
                          >
                            ⚠
                          </span>
                        )}
                      </td>
                      <td>{doc.sortOrder}</td>
                      <td>
                        <span className={`admin-badge ${doc.isActive ? 'approved' : 'rejected'}`}>
                          {doc.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                          <button
                            className="admin-btn admin-btn-outline admin-btn-sm"
                            onClick={() => handleEdit(doc)}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                          >
                            Edit
                          </button>
                          <button
                            className={`admin-btn admin-btn-sm ${doc.isActive ? 'admin-btn-outline' : 'admin-btn-primary'}`}
                            onClick={() => handleToggleActive(doc)}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                          >
                            {doc.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="admin-btn admin-btn-outline admin-btn-sm"
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.375rem' }}
                            title="Move up"
                          >
                            ↑
                          </button>
                          <button
                            className="admin-btn admin-btn-outline admin-btn-sm"
                            onClick={() => handleMoveDown(index)}
                            disabled={index === sortedDocuments.length - 1}
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.375rem' }}
                            title="Move down"
                          >
                            ↓
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
